/**
 * Histórico de uploads e envio de planilha.
 *
 * Esta tela responde à pergunta que o R nunca respondeu: *o que entrou, o que
 * não entrou e por quê?* Por isso as contagens aparecem inteiras, incluindo a
 * invariante — `lidas = aceitas + pendentes`. Se ela deixar de fechar, existe
 * um caminho de descarte silencioso em algum lugar, e a tela mostra isso em vez
 * de esconder.
 */

import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Botao } from '../../components/Botao'
import { Cartao } from '../../components/Cartao'
import { Aviso, Carregando, Erro } from '../../components/Estado'
import { Pagina, Pilha } from '../../components/Pagina'
import { Tabela, type Coluna } from '../../components/Tabela'
import { contagem, dataHora } from '../../lib/formato'
import type { Upload } from '../../lib/tipos'
import { useSessao } from '../../stores/sessao'
import { useEnviarPlanilha, useRemoverUpload, useUploads } from './hooks'
import estilos from './Ingestao.module.css'

export function UploadsPage() {
  const navegar = useNavigate()
  const usuario = useSessao((estado) => estado.usuario)
  const podeEnviar = Boolean(usuario?.podeIngerir || usuario?.isStaff)

  const uploads = useUploads()
  const remover = useRemoverUpload()

  const colunas: Coluna<Upload>[] = [
    {
      chave: 'arquivo',
      cabecalho: 'Arquivo',
      celula: (upload) => (
        <div className={estilos.arquivo}>
          <span className={estilos.arquivoNome}>{upload.nomeOriginal}</span>
          <span className={estilos.arquivoMeta}>
            {dataHora(upload.enviadoEm)} · {upload.enviadoPor}
          </span>
        </div>
      ),
    },
    {
      chave: 'status',
      cabecalho: 'Status',
      largura: '110px',
      celula: (upload) => <Selo status={upload.status} />,
    },
    {
      chave: 'lidas',
      cabecalho: 'Linhas lidas',
      tipo: 'numero',
      celula: (upload) => contagem(upload.linhasLidas),
    },
    {
      chave: 'aceitas',
      cabecalho: 'Aceitas',
      tipo: 'numero',
      celula: (upload) => contagem(upload.linhasAceitas),
    },
    {
      chave: 'pendentes',
      cabecalho: 'Pendentes',
      tipo: 'numero',
      celula: (upload) => (
        <span className={upload.linhasPendentes > 0 ? estilos.pendente : undefined}>
          {contagem(upload.linhasPendentes)}
        </span>
      ),
    },
    {
      chave: 'fatos',
      cabecalho: 'Fatos gravados',
      tipo: 'numero',
      celula: (upload) => contagem(upload.fatosGravados),
    },
  ]

  // Só quem envia planilha pode desfazer o envio — é o mesmo papel. `stopPropagation`
  // porque a linha inteira é clicável (abre o detalhe), e excluir não pode virar
  // efeito colateral de "ver o upload".
  if (podeEnviar) {
    colunas.push({
      chave: 'acoes',
      cabecalho: '',
      largura: '110px',
      celula: (upload) => (
        <Botao
          tamanho="pequeno"
          variante="perigo"
          carregando={remover.isPending && remover.variables === upload.id}
          onClick={(evento) => {
            evento.stopPropagation()
            if (
              window.confirm(
                `Excluir "${upload.nomeOriginal}"? Isso apaga a importação inteira — os ` +
                  `${contagem(upload.fatosGravados)} fato(s) gravados, o staging e as pendências. ` +
                  `Não dá para desfazer.`,
              )
            ) {
              remover.mutate(upload.id)
            }
          }}
        >
          Excluir
        </Botao>
      ),
    })
  }

  return (
    <Pagina
      titulo="Uploads"
      descricao="Cada envio guarda a planilha como veio. Nada é descartado em silêncio: linha que não normaliza vira pendência com o motivo."
    >
      {podeEnviar ? (
        <Enviar />
      ) : (
        <Aviso>
          Seu usuário é somente leitura. Envio de planilha e edição de de-paras exigem a permissão
          <strong> pode enviar planilhas</strong>.
        </Aviso>
      )}

      {uploads.isPending && <Carregando rotulo="Carregando o histórico…" />}
      {uploads.isError && <Erro erro={uploads.error} aoTentarDeNovo={() => uploads.refetch()} />}

      {uploads.data && (
        <Pilha>
          {remover.isError && <Erro erro={remover.error} />}
          <Cartao
            titulo="Histórico"
            subtitulo={
              <>
                {contagem(uploads.data.count)} envio(s). A contagem de fatos não bate com a de
                linhas de propósito — a aba <code>ESALQ-ETANOL</code> empilha cinco séries por
                linha.
              </>
            }
          >
            <Tabela
              colunas={colunas}
              linhas={uploads.data.results}
              chaveDe={(upload) => upload.id}
              rotulo="Histórico de uploads"
              aoClicar={(upload) => navegar(`/uploads/${upload.id}`)}
              vazio="Nenhuma planilha enviada ainda."
            />
          </Cartao>
        </Pilha>
      )}
    </Pagina>
  )
}

function Enviar() {
  const entrada = useRef<HTMLInputElement>(null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const enviar = useEnviarPlanilha()

  function submeter() {
    if (!arquivo) return
    enviar.mutate(arquivo, {
      onSuccess: () => {
        setArquivo(null)
        if (entrada.current) entrada.current.value = ''
      },
    })
  }

  const resultado = enviar.data

  return (
    <Cartao
      titulo="Enviar planilha"
      subtitulo={
        <>
          Arquivos .xlsx. Abas sem schema conhecido são registradas, não ignoradas em silêncio. Não
          sabe o formato?{' '}
          <a className={estilos.modeloLink} href="/api/ingestao/modelo" download>
            Baixar modelo (.xlsx)
          </a>
          .
        </>
      }
    >
      <div className={estilos.enviar}>
        <input
          ref={entrada}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className={estilos.arquivoEntrada}
          onChange={(evento) => setArquivo(evento.target.files?.[0] ?? null)}
        />
        <Botao
          variante="primario"
          onClick={submeter}
          disabled={!arquivo}
          carregando={enviar.isPending}
        >
          {enviar.isPending ? 'Processando…' : 'Enviar'}
        </Botao>
      </div>

      {enviar.isPending && (
        <p className={estilos.nota}>
          O processamento é síncrono. O maior arquivo real tem ~25.700 linhas e leva alguns
          segundos — não feche a aba.
        </p>
      )}

      {enviar.isError && <Erro erro={enviar.error} />}

      {resultado && (
        <Aviso tom={resultado.linhasPendentes > 0 ? 'atencao' : 'bom'}>
          <span>
            <strong>{resultado.nomeOriginal}</strong> — {contagem(resultado.linhasLidas)} linhas
            lidas, {contagem(resultado.linhasAceitas)} aceitas,{' '}
            {contagem(resultado.linhasPendentes)} pendentes,{' '}
            {contagem(resultado.fatosGravados)} fatos gravados.
            {resultado.abasIgnoradas.length > 0 && (
              <>
                {' '}
                Abas sem schema conhecido: {resultado.abasIgnoradas.join(', ')}.
              </>
            )}
          </span>
        </Aviso>
      )}
    </Cartao>
  )
}

const ROTULO_STATUS: Record<Upload['status'], string> = {
  recebido: 'Recebido',
  processando: 'Processando',
  concluido: 'Concluído',
  erro: 'Erro',
}

/**
 * Selo de status.
 *
 * Cor **e** texto. Um selo que fosse só um ponto colorido faria o estado
 * depender de cor sozinha — que é a barreira de acessibilidade mais comum em
 * painel, e a que mais some numa projeção.
 */
export function Selo({ status }: { status: Upload['status'] }) {
  return <span className={`${estilos.selo} ${estilos[status]}`}>{ROTULO_STATUS[status]}</span>
}
